import { RoleVO } from './role.vo';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { Profile, Role, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { TranslatableLabelVO } from '../common/translation/translatable-label.vo';
import { entity, field } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';

/** un profil utilisateur */
@entity('wizeoo.allkey.user.message.Profile', 'BU')
export class ProfileVO {
  /**
   * Le VO source provenant du back
   */
  private _source: Profile;

  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }

  constructor(model: Model, pSource?: Profile) {
    this._model = model;
    this._source = null;
    if (pSource) {
      this._source = pSource;
    } else {
      this._source = new Profile();
      this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
    }
  }

  public set source(profile: Profile) {
    this._source = profile;
  }
  public get source(): Profile {
    return this._source;
  }

  public set uid(uid: string) {
    this._source.uid = uid;
  }
  public get uid(): string {
    return this._source.uid;
  }

  // Id technique
  public get id(): number {
    return this._source.id;
  }
  public set id(value: number) {
    this._source.id = value;
  }

  public set domain(domain: string) {
    this._source.domain = domain;
  }

  public get domain(): string {
    return this._source.domain;
  }

  @field('string')
  public set code(code: string) {
    this._source.code = code ? code.toUpperCase() : code;
  }
  public get code(): string {
    return this._source.code;
  }

  @field('TranslatedEmb')
  public set label(value: TranslatedEmb) {
    this._source.label = value;
  }
  public get label(): TranslatedEmb {
    return this._source.label;
  }

  @field('boolean')
  public get deprecated(): boolean {
    return this._source.deprecated;
  }
  public set deprecated(value: boolean) {
    this._source.deprecated = value;
  }

  @field('boolean')
  public get deleted(): boolean {
    return this._source.deleted;
  }
  public set deleted(value: boolean) {
    this._source.deleted = value;
  }

  @field('string')
  public set visionCode(value: string) {
    this._source.visionCode = value;
  }
  public get visionCode(): string {
    return this._source.visionCode;
  }

  @field('string')
  public set perimeter(value: string) {
    this._source.perimeter = value;
  }
  public get perimeter(): string {
    return this._source.perimeter;
  }

  public set businessUnitCode(businessUnitCode: string) {
    this._source.businessUnitCode = businessUnitCode;
  }
  public get businessUnitCode(): string {
    return this._source.businessUnitCode;
  }

  /** collection de rôles */
  @field('Role', true)
  public get roles(): Role[] {
    if (!this._source.roles) {
      this._source.roles = [];
    }
    return this._source.roles;
  }
  public set roles(roles: Role[]) {
    this._source.roles = roles;
  }

  /** Libellé traductible */
  private _labelVO: TranslatableLabelVO;
  public get labelVO(): TranslatableLabelVO {
    if (!this._labelVO) {
      this._labelVO = this.model.localeEngine.newTranslatableVO(this, 'label');
    }
    return this._labelVO;
  }
  
  @field('string')
  public get tags(): Object {
    return this._source.tags;
  }
  public set tags(value: Object) {
    this._source.tags = value;
  }

  /* ################################################
                  Accesseurs composites
################################################ */

  /**
   * Accès au libellé traduit
   * @return string libellé traduit dans la langue locale
   */
  public get trsLabel(): string {
    return this.model.localeEngine.translate(this.label, this.code);
  }

  /**
   * l'objet existe en BDD (id)
   */
  public get exist(): boolean {
    return this.id > 0;
  }

  private _roles: RoleVO[];

  /**
   * Accès aux roles du profile
   */
  public get rolesVO(): RoleVO[] {
    if (!this._roles || this._roles.length !== this.roles.length) {
      this._roles = [];

      if (!!this.roles && this.roles.length > 0) {
        this._roles = this.roles.map((r) => new RoleVO(this.model, r));
      }
    }

    return this._roles;
  }

  /**
   * Setter de la liste de roles en mode VO
   * @param value la liste de roles
   */
  public set rolesVO(value: RoleVO[]) {
    let result: Role[] = null;
    if (value) {
      result = value.map((cn) => cn.source);
    }
    this.roles = result;
  }

  /**
   * Le profile contient-il le rôle en paramètre?
   * @param pRole Le role testé
   * @return true si le profil a le role en paramètre
   */
  public hasRole(pRole: RoleVO): boolean {
    let result: boolean = false;
    for (const role of this.roles) {
      if (role.code === pRole.code) {
        result = true;
        break;
      }
    }
    return result;
  }

  /**
   * Ajout du rôle à l'app
   * @param role le rôle à ajouters
   */
  public addRole(role: RoleVO): void {
    this.roles.push(role.source);
    this.rolesVO.push(role);
  }

  /**
   * Supprime le rôle pour le code donnée en paramètre
   * @param code le code du rôle à supprimer
   */
  public removeRole(code: string) {
    // pas de rôles, on ne va pas plus loin
    if (!this.rolesVO || this.rolesVO.length === 0) {
      return;
    }

    let roleIndex = this.roles.findIndex((r) => r.code === code);
    if (roleIndex >= 0) {
      this.roles.splice(roleIndex, 1);
    }

    roleIndex = this._roles.findIndex((r) => r.code === code);
    if (roleIndex >= 0) {
      this._roles.splice(roleIndex, 1);
    }
  }

  /** Rendu Code- label */
  public get codeAndLabelStr(): string {
    const label = this.trsLabel ? this.trsLabel : '';
    return this.code + ' - ' + label;
  }
}
